import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import style from './view-profile-popover.module.scss';
import './view-profile-popover.module.scss';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Snackbar, SnackbarOrigin } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import UserPost from '../user-posts/user-post-list';
import { useContext, useEffect, useState } from 'react';
import { WButton, WTooltip, WTypography } from '../../wrappers/wrapper';
import ProfilePhoto from '../../molecules/profile-photo/profile-photo';
import ViewProfilePopoverSkeleton from '../../organisms/view-profile-popover-skeleton/view-profile-popover-skeleton';
import ProfileDialog from '../profile-dialog/profile-dialog';
import UserComments from '../user-comments/user-comments-list';
import { NavLink } from 'react-router-dom';
import { UserContext } from '../../../contexts/userContext';
import { DynamicIcon } from '../../atoms/dynamic-icon/dynamic-icon';
import { TChannel, TSpace, TUser } from '../../../models/common';
import dbService from '../../../services/database.service';
import helperService from '../../../services/helper.service';
import { ProjectContext } from 'contexts/projectContext';

enum Tabs {
  ABOUT = 'About',
  POSTS = 'Posts',
  COMMENTS = 'Comments',
  SPACES = 'Spaces',
}

interface IViewProfileProps {
  onClose: () => void;
  user_id: string;
  type?: 'drawer' | 'page';
  showEditButton: boolean;
}
interface State extends SnackbarOrigin {
  open: boolean;
}

const profileDetails = [
  {
    text: 'Bio',
    property: (userData: any) => userData?.biography,
  },
  {
    text: 'Location',
    property: (userData: any) => userData?.location?.coordinates,
  },
  {
    text: 'Website',
    property: (userData: any) => userData?.website,
  },
  {
    text: 'Company',
    property: (userData: any) => userData?.company,
  },
  {
    text: 'Profession',
    property: (userData: any) => userData?.profession,
  },
];

function ViewProfile({ user_id, type = 'drawer', onClose, showEditButton }: IViewProfileProps) {
  const { setCurrentPage } = useContext(UserContext);
  const { channelsMembersCounts } = useContext(ProjectContext);

  const [userData, setUserData] = useState<TUser>();
  const [indexOfTab, setIndexOfTab] = useState<0 | 1 | 2 | 3>(0);
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [spaces, setSpaces] = useState<TSpace[]>([]);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    initializeData();
  }, [user_id]);

  const initializeData = async () => {
    setLoading(true);
    const userDataRes = await getUserData();
    if(type === "page"){
      document.title = userDataRes.username!;
    }
    
    const userSpacesRes = await getUserSpaces(userDataRes._id!);
    prepareTabs(userSpacesRes);
    setUserData(userDataRes);
    setSpaces(userSpacesRes);
    setLoading(false);
  };

  const getUserData = () => {
    return dbService.getUsers({ relation: true, filter: { _id: user_id } }).then((res) => res[0]);
  };

  const getUserSpaces = (userId: string) => {
    return dbService.getSpaces({ filter: { user: userId }, relation: ['channel'] });
  };

  const prepareTabs = (userSpaces: TSpace[]) => {
    if (userSpaces?.length) {
      setTabs(Object.values(Tabs) as []);
      return;
    }

    const _tabs = Object.values(Tabs).filter((el) => el !== Tabs.SPACES);
    setTabs(_tabs as []);
  };

  const handleOpenProfileDialog = (tabIndex: number) => {
    setActiveTab(tabIndex);
    setOpenProfileDialog(true);
  };

  const handleCloseProfileDialog = () => {
    setOpenProfileDialog(false);
  };
  const handleChangeTab = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [state, setState] = useState<State>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal, open } = state;

  const handleClick = (newState: SnackbarOrigin) => () => {
    setState({ ...newState, open: true });
  };

  const handleClose = () => {
    setState({ ...state, open: false });
    onClose();
  };

  const showProfileDetails = () => {
    return profileDetails.some((details) => details.property(userData));
  };

  const getMembersCountOfSpace = (id: string) => {
    return channelsMembersCounts.find((el) => el.id === id)?.count || 0;
  };

  const tabsContent = {
    0: () => (
      <div className={style['wtab-list']}>
        <div className={style.list}>
          <div className={style['list-item']}>
            <AlternateEmailIcon />
            {userData ? userData.email : 'Empty'}
          </div>
          <div className={style['list-item']}>
            <CalendarMonthIcon />
            {userData
              ? 'Members since: ' +
                new Date(String(userData.created_at)).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : 'Empty'}
          </div>
        </div>
        {!!showProfileDetails() && (
          <div className={style.list}>
            {profileDetails.map((details, index) =>
              details.property(userData) ? (
                <div className={style['list-item']} key={index}>
                  <div className={style['details']}>
                    <span className={style['title']}>{details.text}</span>
                    <span className={style['sub-title']}>
                      {userData && details.property(userData)}
                    </span>
                  </div>
                </div>
              ) : null
            )}
          </div>
        )}
      </div>
    ),
    1: () => <UserPost userId={user_id} handleClose={handleClose} />,
    2: () => <UserComments userId={user_id} handleClose={handleClose} />,
    3: () => (
      <div className={style['spaces-container']}>
        {spaces.map((space, index) => {
          const channel = space.channel as unknown as TChannel;
          return (
            <NavLink
              key={index}
              onClick={() => {
                setCurrentPage({
                  title: channel.title,
                  icon: channel.pageicon,
                });
              }}
              className={style['channel-link']}
              to={'/c/' + channel.path}
            >
              <div className={style['channel-contain']}>
                <DynamicIcon iconName={String(channel.pageicon)} />
                <div className={style['title']}>
                  <span className={style['channel-title']}>{channel.title}</span>
                  <span className={style['user-count']}>
                    {getMembersCountOfSpace(channel._id!)} members
                  </span>
                </div>
              </div>
            </NavLink>
          );
        })}
      </div>
    ),
  };

  if (!user_id) return null;

  const renderContent = () => (
    <>
      {loading ? (
        <ViewProfilePopoverSkeleton />
      ) : (
        <>
          <Box className={style['top-box']}>
            <div className={style['profile-photo-container']}>
              <ProfilePhoto
                user={userData}
                width="6rem"
                height="6rem"
                fontSize="2rem"
                fontWeight="500"
              />
            </div>
            <div className={style['top']}>
              <WTypography className={style['username-text']}>
                {userData ? userData.username : 'Empty'}
              </WTypography>
              <WTypography className={style.subTitle}>
                {userData?.job}
                {userData?.company && <> @ {userData?.company}</>}
              </WTypography>
              <div className={style['tooltip-wrapper']}>
                <WTooltip title="Copy Profile Link" placement="bottom">
                  <div>
                    <WButton
                      className={style['text-copy-profile']}
                      onClick={handleClick({
                        vertical: 'bottom',
                        horizontal: 'center',
                      })}
                      variant="outlined"
                    >
                      <CopyToClipboard
                        text={
                          process.env.REACT_APP_URL +
                          helperService.getNavigateLink(userData ? userData : {})
                        }
                      >
                        <div className={style['copyToClipboard']}>
                          <LinkIcon />
                        </div>
                      </CopyToClipboard>
                    </WButton>
                  </div>
                </WTooltip>
                <Snackbar
                  anchorOrigin={{ vertical, horizontal }}
                  open={open}
                  onClose={handleClose}
                  message="Link copied to clipboard!"
                  key={vertical + horizontal}
                />
                {showEditButton !== false && (
                  <WTooltip title="Edit Profile" placement="bottom">
                    <div>
                      <WButton
                        className={style['edit-profile-button']}
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenProfileDialog(0)}
                      >
                        <EditIcon />
                      </WButton>
                    </div>
                  </WTooltip>
                )}
                <ProfileDialog
                  open={openProfileDialog}
                  onClose={handleCloseProfileDialog}
                  activeTab={activeTab}
                  onTabChange={handleChangeTab}
                />
              </div>
            </div>
          </Box>
          <div className={style.tabs}>
            {tabs.map((tab, index) => (
              <WTypography
                key={index}
                className={`${style.tab} ${index === indexOfTab && style.active}`}
                onClick={() => setIndexOfTab(index as any)}
              >
                {tab}
              </WTypography>
            ))}
          </div>
          <div className={indexOfTab === 3 ? '' : style.tabContent}>
            {tabsContent[indexOfTab]()}
          </div>
        </>
      )}
    </>
  );

  const typeObj = {
    drawer: (
      <Drawer anchor={'right'} open={true} onClose={handleClose}>
        <div className={style.viewProfile}>
          <div className={style.header}>
            <span>Profile</span>
            <CloseIcon
              onClick={() => {
                onClose();
              }}
              className={style.closeIcon}
            />
          </div>
          {renderContent()}
        </div>
      </Drawer>
    ),
    page: renderContent(),
  };

  if (!user_id) return null;

  return <div>{typeObj[type]}</div>;
}

export default ViewProfile;
